import "./SimpleTable.scss";

import {
  Loader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from "@trackman/web-shared-components";

import { Column } from "./WSC-v2/tableHooks.models";
import { FC } from "react";
import { FiltersData } from "components/TableFilters/TableFilters";
import { Sort } from "./Sort";
import { tableHooks } from "./WSC-v2/tableHooks";

interface STProps {
  columns: Column[];
  data: any[];
  page: number;
  count?: number;
  rowsPerPage: number;
  onChangePage: (page: number) => void;
  rowMapper: (cells: any[]) => JSX.Element;
  onHeaderClick?: (columnKey: string, defaultClick?: () => void) => void;
  sortedColumn: string;
  sortType: Sort;
  filters?: FiltersData;
  isLoading: boolean;
}

export const SimpleTable: FC<STProps> = (props) => {
  const {
    rowMapper,
    columns,
    data,
    page,
    rowsPerPage,
    onChangePage,
    onHeaderClick,
    sortedColumn,
    sortType,
    filters,
    isLoading,
  } = props;
  const { useTable, useSortBy, usePagination, useFlexLayout } = tableHooks;
  const { getTableProps, rows, headerGroups, prepareRow } = useTable(
    {
      columns: columns,
      data: data,
    },
    useSortBy,
    usePagination,
    useFlexLayout
  );

  return (
    <TableContainer>
      <>
        <div>{filters?.current}</div>
        <Table {...getTableProps()} style={{}} className="simple-table">
          <TableHead>
            {headerGroups.map(({ headers, getHeaderGroupProps }) => (
              <TableRow {...getHeaderGroupProps()} style={{}}>
                {headers.map(
                  (
                    {
                      sortable,
                      isSorted,
                      isSortedDesc,
                      align,
                      render,
                      getHeaderProps,
                      getSortByToggleProps,
                    },
                    index
                  ) => {
                    const columnKey = getHeaderProps().key.split("_")[1];
                    return onHeaderClick && sortable ? (
                      <TableCell
                        {...getHeaderProps(sortable && getSortByToggleProps())}
                        sortable={sortable}
                        onClick={(e) =>
                          onHeaderClick(columnKey, () =>
                            getSortByToggleProps().onClick(e)
                          )
                        }
                        sortDirection={
                          sortedColumn === columnKey ? sortType : false
                        }
                        align={align}
                      >
                        <TableSortLabel
                          active={sortedColumn === columnKey}
                          direction={sortType}
                        >
                          <div className="simple-table-header">
                            {render("Header")}
                            {filters?.contentIcons[index]}
                          </div>
                        </TableSortLabel>
                      </TableCell>
                    ) : (
                      <TableCell
                        {...getHeaderProps(sortable && getSortByToggleProps())}
                        sortable={sortable}
                        sortDirection={
                          isSorted ? (isSortedDesc ? "desc" : "asc") : false
                        }
                        align={align}
                      >
                        <div className="simple-table-header">
                          {render("Header")}
                          {filters?.contentIcons[index]}
                        </div>
                      </TableCell>
                    );
                  }
                )}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <div
                    style={{
                      height: "455px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Loader size="large" />
                  </div>
                </TableCell>
              </TableRow>
            ) : rows.length !== 0 ? (
              rows.map((row, index) => {
                prepareRow(row);
                return (
                  <TableRow index={index} {...row.getRowProps()} style={{}}>
                    {rowMapper(row.cells)}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  style={{ textAlign: "center", margin: "16px" }}
                >
                  <div
                    style={{
                      textAlign: "center",
                      margin: "50px",
                      justifyContent: "center",
                    }}
                    className="side-title"
                  >
                    NO RESULTS
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                count={page * rows.length + 1} // this component does not support server side pagination, hence + 1 at the end is needed
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[rowsPerPage]}
                onRowsPerPageChange={undefined}
                page={page - 1} // In old table component, page is 1 based, in new one it is 0 based
                onPageChange={(_, p) => onChangePage(p + 1)}
                renderPages={() => <Typography>Page {page}</Typography>}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </>
    </TableContainer>
  );
};
